import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import type { CustomDomain } from '@/api/customDomains/customDomainsApi.types';
import {
  useCheckCustomDomainMutation,
  useMakeCustomDomainPrimaryMutation,
  useRemoveCustomDomainMutation,
} from '@/api/customDomains/useWorkspaceCustomDomains';
import { formatDateToDayMonthYear, formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CustomDomainDnsRecordRow } from './CustomDomainDnsRecordRow';
import { CustomDomainProgress } from './CustomDomainProgress';
import { CustomDomainReasonMessage } from './CustomDomainReasonMessage';
import { CustomDomainStatusBadge } from './CustomDomainStatusBadge';
import {
  canCheckCustomDomain,
  canMakeCustomDomainPrimary,
  hasWwwCompanionDomain,
  resolveCustomDomainSteps,
  shouldCheckAutomatically,
  shouldShowDnsRecords,
} from './customDomainPresentation';

const AUTOMATIC_CHECK_INTERVAL_MILLISECONDS = 60000;

interface CustomDomainCardProps {
  customDomain: CustomDomain;
  allCustomDomains: CustomDomain[];
}

export function CustomDomainCard({ customDomain, allCustomDomains }: CustomDomainCardProps) {
  const { t } = useTranslation('customDomains');
  const checkMutation = useCheckCustomDomainMutation();
  const automaticCheckMutation = useCheckCustomDomainMutation();
  const triggerAutomaticCheck = automaticCheckMutation.mutate;
  const makePrimaryMutation = useMakeCustomDomainPrimaryMutation();
  const removeMutation = useRemoveCustomDomainMutation();
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  const isBusy =
    checkMutation.isPending || makePrimaryMutation.isPending || removeMutation.isPending;
  const isRemoving = customDomain.status === 'removing';
  const isActive = customDomain.status === 'active';
  const isShowingDnsRecords = shouldShowDnsRecords(customDomain);
  const progressSteps = resolveCustomDomainSteps(customDomain);
  const isCheckingAutomatically = shouldCheckAutomatically(customDomain);

  useEffect(
    function checkWhileWaitingForDns() {
      if (isCheckingAutomatically === false) {
        return undefined;
      }
      const intervalHandle = window.setInterval(function checkAgain() {
        triggerAutomaticCheck(customDomain.id);
      }, AUTOMATIC_CHECK_INTERVAL_MILLISECONDS);
      return function stopChecking() {
        window.clearInterval(intervalHandle);
      };
    },
    [isCheckingAutomatically, customDomain.id, triggerAutomaticCheck]
  );

  function notifyActionFailed(actionError: unknown) {
    const backendMessage = resolveApiErrorMessage(actionError);
    showErrorNotification(backendMessage.length > 0 ? backendMessage : t('notices.actionFailed'));
  }

  function checkNow() {
    checkMutation.mutate(customDomain.id, { onError: notifyActionFailed });
  }

  function makePrimary() {
    makePrimaryMutation.mutate(customDomain.id, {
      onSuccess: function notifyMadePrimary() {
        showSuccessNotification(t('notices.madePrimary'));
      },
      onError: notifyActionFailed,
    });
  }

  function confirmRemove() {
    removeMutation.mutate(customDomain.id, {
      onSuccess: function notifyRemoved() {
        showSuccessNotification(t('notices.removed'));
        setIsConfirmingRemove(false);
      },
      onError: function notifyRemoveFailed(removeError) {
        notifyActionFailed(removeError);
        setIsConfirmingRemove(false);
      },
    });
  }

  return (
    <Card padding="none" shadow={false} className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="Globe" className="text-zooviolet" />
        <h3 className="text-sm font-bold text-zooindigo break-all min-w-0">
          {customDomain.displayHostname}
        </h3>
        <CustomDomainStatusBadge status={customDomain.status} />
        {customDomain.isPrimary === true ? (
          <Badge tone="accent" size="sm">
            {t('primaryBadge')}
          </Badge>
        ) : null}
      </div>

      {progressSteps !== null ? (
        <div className="mt-4">
          <CustomDomainProgress steps={progressSteps} />
        </div>
      ) : null}

      {customDomain.status === 'disabled' ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-zoomuted">{t('progress.paused')}</p>
          {customDomain.statusReason !== null ? (
            <CustomDomainReasonMessage reason={customDomain.statusReason} shouldShowFix />
          ) : null}
        </div>
      ) : null}

      {isRemoving === true ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-zoomuted">
          <LoadingSpinner className="w-4 h-4" />
          {t('progress.removing')}
        </div>
      ) : null}

      {isShowingDnsRecords === true ? (
        <div className="mt-4 space-y-3">
          <div>
            <h4 className="text-xs font-bold text-zooindigo">{t('dns.title')}</h4>
            <p className="text-xs text-zoomuted mt-0.5">{t('dns.description')}</p>
          </div>
          {customDomain.dnsRecords.map(function renderRecord(record) {
            return (
              <CustomDomainDnsRecordRow
                key={record.purpose + record.recordType + record.name}
                record={record}
              />
            );
          })}
          {customDomain.kind === 'root' ? (
            <p className="text-xs text-zoowarningtext">{t('dns.rootNote')}</p>
          ) : null}
          <p className="text-xs text-zoomuted">{t('dns.propagationNote')}</p>
        </div>
      ) : null}

      {customDomain.kind === 'root' &&
      isRemoving === false &&
      hasWwwCompanionDomain(customDomain, allCustomDomains) === false ? (
        <p className="mt-3 text-xs text-zooindigo bg-zoovpalelavender rounded-xl p-3">
          {t('dns.wwwHint', { hostname: customDomain.displayHostname })}
        </p>
      ) : null}

      {isActive === true && customDomain.certificateExpiresAt !== null ? (
        <p className="mt-3 text-xs text-zoomuted">
          {t('certificateExpires', {
            date: formatDateToDayMonthYear(parseISO(customDomain.certificateExpiresAt)),
          })}
        </p>
      ) : null}

      {customDomain.lastCheckedAt !== null && isActive === false ? (
        <p className="mt-1 text-[11px] text-zoomuted">
          {t('lastChecked', { time: formatIsoDateTimeToTimestamp(customDomain.lastCheckedAt) })}
        </p>
      ) : null}

      {isRemoving === false ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {isActive === true ? (
            <Button asChild size="chip" variant="outlinePrimary">
              <a
                href={'https://' + customDomain.hostname}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="Link" />
                {t('actions.open', { hostname: customDomain.displayHostname })}
              </a>
            </Button>
          ) : null}
          {canCheckCustomDomain(customDomain) === true ? (
            <Button
              type="button"
              size="chip"
              variant="outlinePrimary"
              disabled={isBusy}
              isLoading={checkMutation.isPending}
              onClick={checkNow}
              className="gap-1.5"
            >
              {checkMutation.isPending ? null : <Icon name="RefreshCw" />}
              {t('actions.checkNow')}
            </Button>
          ) : null}
          {canMakeCustomDomainPrimary(customDomain, allCustomDomains.length) === true ? (
            <Button
              type="button"
              size="chip"
              variant="outline"
              disabled={isBusy}
              isLoading={makePrimaryMutation.isPending}
              onClick={makePrimary}
              className="gap-1.5"
            >
              {makePrimaryMutation.isPending ? null : <Icon name="Star" />}
              {t('actions.makePrimary')}
            </Button>
          ) : null}
          <Button
            type="button"
            size="chip"
            variant="destructiveGhost"
            disabled={isBusy}
            onClick={function requestRemove() {
              setIsConfirmingRemove(true);
            }}
            className="gap-1.5 sm:ml-auto"
          >
            <Icon name="Trash" />
            {t('actions.remove')}
          </Button>
        </div>
      ) : null}

      {isConfirmingRemove === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('removeConfirm.title')}
          description={t('removeConfirm.description', { hostname: customDomain.displayHostname })}
          confirmLabel={t('removeConfirm.confirm')}
          isConfirming={removeMutation.isPending}
          onConfirm={confirmRemove}
          onCancel={function cancelRemove() {
            setIsConfirmingRemove(false);
          }}
        />
      ) : null}
    </Card>
  );
}
